<script setup lang="ts">
/**
 * SkipLink - Accessibility link for keyboard navigation
 *
 * Provides a "skip to main content" link that is visually hidden
 * but becomes visible when focused. This allows keyboard users to
 * skip past navigation and jump directly to the main content area.
 */
import { useI18n } from 'vue-i18n'

interface Props {
  /** The target element ID to skip to (without #) */
  target?: string
}

withDefaults(defineProps<Props>(), {
  target: 'main-content'
})

const { t } = useI18n()
</script>

<template>
  <a :href="`#${target}`" class="skip-link">
    {{ t('accessibility.skipToMain') }}
  </a>
</template>

<style scoped>
.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-background-surface);
  color: var(--color-text-primary);
  text-decoration: none;
  z-index: 100;
  transition: top var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.skip-link:focus {
  top: 0;
  outline: 2px solid var(--color-border-input-focus);
  outline-offset: 2px;
}
</style>
